import { CornerDownLeft, Info, X } from 'lucide-react';
import type { ChangeEvent, KeyboardEvent, ReactNode } from 'react';

import {
  getPlatformName,
  PLATFORM_LOGO_BY_TYPE,
  type SOURCE_PLATFORM_TYPE,
} from '@/entities/public-filter/public-filter-constants';
import { Badge } from '@/shared/components/shadcn/badge';
import { Input } from '@/shared/components/shadcn/input';
import { TooltipFlat } from '@/shared/components/shadcn/tooltip/tooltip-flat';
import { cn } from '@/shared/utils';

export function FieldSection({
  title,
  titleSuffix,
  trailing,
  children,
}: {
  title: string;
  titleSuffix?: ReactNode;
  trailing?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="space-y-0">
      <div className="flex min-h-8 items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-1.5">
          <h3 className="text-foreground text-sm font-medium">{title}</h3>
          {titleSuffix}
        </div>
        {trailing ? <div className="flex h-8 items-center">{trailing}</div> : null}
      </div>
      {children}
    </div>
  );
}

export function StepSubsectionTitle({ title }: { title: string }) {
  return <h2 className="text-foreground text-base font-semibold tracking-tight">{title}</h2>;
}

export function FormSectionHeader({
  title,
  titleSuffix,
  description,
}: {
  title: string;
  titleSuffix?: ReactNode;
  description: string;
}) {
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-1.5">
        <h2 className="text-foreground text-base font-semibold tracking-tight">{title}</h2>
        {titleSuffix}
      </div>
      <p className="text-muted-foreground text-sm leading-5">{description}</p>
    </div>
  );
}

export function FieldInfoIcon({ ariaLabel, content }: { ariaLabel: string; content: ReactNode }) {
  return (
    <TooltipFlat asChild contentClassName="max-w-65 text-sm leading-5" content={content}>
      <span
        tabIndex={0}
        className="text-muted-foreground hover:text-foreground focus-visible:text-foreground inline-flex size-5 items-center justify-center rounded-full transition-colors outline-none"
        aria-label={ariaLabel}
      >
        <Info className="size-3.5" />
      </span>
    </TooltipFlat>
  );
}

export function PreviewMetric({ label, value }: { label: string; value: string }) {
  return (
    <div className="border-border bg-card rounded-xl border px-4 py-3">
      <div className="text-muted-foreground text-xs font-medium tracking-[0.12em] uppercase">
        {label}
      </div>
      <div className="text-card-foreground mt-1 text-sm font-medium">{value}</div>
    </div>
  );
}

export function PlatformSelectLabel({ platform }: { platform: SOURCE_PLATFORM_TYPE }) {
  const PlatformLogo = PLATFORM_LOGO_BY_TYPE[platform];

  return (
    <span className="inline-flex items-center gap-2">
      <PlatformLogo className="size-4" />
      <span>{getPlatformName(platform)}</span>
    </span>
  );
}

export function CampaignKeywordChip({
  className,
  label,
  onRemove,
  title,
}: {
  className?: string;
  label: string;
  onRemove?: () => void;
  title?: string;
}) {
  return (
    <Badge
      variant="secondary"
      className={cn(
        'text-foreground flex h-7 max-w-full items-center gap-1.5 rounded-full px-2.5 py-1 text-[12px] font-medium',
        onRemove && 'pr-1.5',
        className,
      )}
      title={title ?? label}
    >
      <span className="truncate">{label}</span>
      {onRemove ? (
        <button
          type="button"
          onClick={onRemove}
          className="text-muted-foreground hover:text-foreground inline-flex size-3.5 items-center justify-center rounded-full transition-colors"
          aria-label={`Remove ${label}`}
        >
          <X className="size-3" />
        </button>
      ) : null}
    </Badge>
  );
}

export function CampaignKeywordInputCanvas({
  chipsContainerClassName,
  disabled = false,
  inputValue,
  keywords,
  onBlur,
  onChange,
  onKeyDown,
  onRemoveKeyword,
}: {
  chipsContainerClassName?: string;
  disabled?: boolean;
  inputValue: string;
  keywords: string[];
  onBlur: () => void;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  onKeyDown: (event: KeyboardEvent<HTMLInputElement>) => void;
  onRemoveKeyword: (keyword: string) => void;
}) {
  return (
    <div className="border-border bg-card focus-within:border-ring focus-within:ring-ring/50 relative rounded-2xl border px-4 py-3 transition-[color,box-shadow] focus-within:ring-[3px]">
      {inputValue.trim() ? (
        <span className="border-border bg-muted text-muted-foreground pointer-events-none absolute top-3 right-4 inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs">
          <CornerDownLeft className="size-3" />
          Enter to add
        </span>
      ) : null}

      <div
        className={cn(
          'flex min-h-12 flex-wrap content-start items-start gap-2 pr-28',
          chipsContainerClassName,
        )}
      >
        {keywords.map((keyword) => (
          <CampaignKeywordChip
            key={keyword}
            label={keyword}
            onRemove={disabled ? undefined : () => onRemoveKeyword(keyword)}
          />
        ))}

        <Input
          value={inputValue}
          onChange={onChange}
          onKeyDown={onKeyDown}
          onBlur={onBlur}
          placeholder="keyword, #hashtag, @mention"
          disabled={disabled}
          className="h-9 min-w-45 flex-1 border-0 bg-transparent px-0 py-0 text-sm leading-6 shadow-none placeholder:text-sm focus-visible:border-transparent focus-visible:ring-0"
        />
      </div>
    </div>
  );
}

import { TabsList, TabsTrigger } from '@/shared/components/shadcn/tabs';
import type { CampaignEditorSection } from './campaign-editor-types';

const tabs: Array<{
  label: string;
  value: CampaignEditorSection;
}> = [
  {
    label: 'Campaign info',
    value: 'campaign-info',
  },
  {
    label: 'Creators & Keywords',
    value: 'creators',
  },
  {
    label: 'Post URLs',
    value: 'post-urls',
  },
];

export function CampaignEditorPhaseTabs({ setupReady }: { setupReady: boolean }) {
  return (
    <TabsList className="grid w-full max-w-170 grid-cols-3">
      {tabs.map((section) => (
        <TabsTrigger
          key={section.value}
          value={section.value}
          disabled={section.value !== 'campaign-info' && !setupReady}
        >
          {section.label}
        </TabsTrigger>
      ))}
    </TabsList>
  );
}
